import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  updateDoc,
  type Unsubscribe,
} from "firebase/firestore";
import type { Project } from "@shared/types";
import type { Invoice } from "@shared/types";
import { projectBasePath } from "@domain/project/paths";
import { db } from "../firebase";

/**
 * Per-project invoices at
 *   <projectBasePath>/invoices/{id}
 *
 * Real subcollection (not a single doc array) because invoices grow
 * unbounded and embed extraction metadata that's expensive to rewrite on
 * every keystroke. Caller supplies a stable id (e.g. crypto.randomUUID).
 */
export const invoiceRepository = {
  /** Subscribe to all invoices for a project, newest first by dateIssued. */
  watch(
    project: Project,
    onChange: (items: Invoice[]) => void
  ): Unsubscribe {
    const col = collection(db, projectBasePath(project), "invoices");
    const q = query(col, orderBy("createdAt", "desc"));
    return onSnapshot(
      q,
      (snap) => {
        // Always trust the document id over any stored `id` field — guards
        // against React key collisions if the stored field is missing/stale.
        onChange(snap.docs.map((d) => ({ ...(d.data() as Invoice), id: d.id })));
      },
      (err) => {
        console.error("[invoiceRepository.watch]", err);
        onChange([]);
      }
    );
  },

  async add(project: Project, invoice: Invoice): Promise<void> {
    const col = collection(db, projectBasePath(project), "invoices");
    await setDoc(doc(col, invoice.id), invoice);
  },

  async update(
    project: Project,
    invoiceId: string,
    patch: Partial<Invoice>
  ): Promise<void> {
    await updateDoc(
      doc(db, projectBasePath(project), "invoices", invoiceId),
      patch as Record<string, unknown>
    );
  },

  async remove(project: Project, invoiceId: string): Promise<void> {
    await deleteDoc(
      doc(db, projectBasePath(project), "invoices", invoiceId)
    );
  },
};
